import { useUserSetting } from '../../context/auth/auth-hooks';
import { type QuotePriceProperties } from '../types/quote';

/**
 * @param premium_value - The premium value. Can be in a different currency than the default currency.
 * @param premium_currency - The currency of the premium value. Can be different than the default currency.
 * @param converted_customer_premium_value - The premium value in the default currency.
 * @param converted_customer_premium_currency - The default currency.
 * @param tax - The tax value. Can be in a different currency than the default currency.
 * @param converted_tax_amount - The tax value in the default currency.
 * @param converted_tax_currency - The default currency.
 *
 * @returns the premium, tax and total amounts in the correct currency based on user setting.
 */
export const useQuoteCosts = ({
  premium_value = 0,
  premium_currency = '',
  converted_customer_premium_value = 0,
  converted_customer_premium_currency = '',
  tax,
  converted_tax_amount = 0,
  converted_tax_currency = '',
}: QuotePriceProperties) => {
  const showPremiumsInDefaultCurrency = useUserSetting<boolean>(
    'show_premiums_in_default_currency',
  );

  const shouldUsePremiumInDefaultCurrency =
    showPremiumsInDefaultCurrency && converted_customer_premium_value;

  const premiumCost = shouldUsePremiumInDefaultCurrency
    ? converted_customer_premium_value
    : premium_value;
  const premiumCurrency = shouldUsePremiumInDefaultCurrency
    ? converted_customer_premium_currency
    : premium_currency;

  const taxCost = shouldUsePremiumInDefaultCurrency
    ? converted_tax_amount
    : tax?.value || 0;
  const taxCurrency = shouldUsePremiumInDefaultCurrency
    ? converted_tax_currency
    : tax?.currency_code;

  return {
    premiumCost,
    premiumCurrency,
    taxCost,
    taxCurrency,
    totalCost: premiumCost + taxCost,
  };
};
